/**
 * Feature-Gate Service — Plan-aware Feature-Toggle-Management.
 *
 * Web-State leitet Plan-/Toggle-Metadaten aus `practices` ab und ergänzt
 * kanonische Backend-Entitlements über `check_feature_enabled()`.
 */
import { supabase, isSupabaseConfigured, requireSupabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type {
    PracticeFeatureKey,
    FeatureStatus,
    FeatureStatusMap,
    SubscriptionPlan,
    PracticeFeatures,
} from "@/types/practice";
import {
    PLAN_FEATURES,
    FEATURE_KEY_TO_DB,
    PRACTICE_FEATURE_KEYS,
    CONFIGURABLE_FEATURE_KEYS,
    isAddonActivatedPracticeFeature,
} from "@/types/practice";

const LOG = "[feature-gate.service]";

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/** Map DB snake_case feature JSONB to camelCase PracticeFeatures */
function dbFeaturesToCamel(dbFeatures: Record<string, unknown> | null): Partial<PracticeFeatures> {
    if (!dbFeatures) return {};

    const result: Partial<PracticeFeatures> = {};
    const reverseMap = Object.fromEntries(
        Object.entries(FEATURE_KEY_TO_DB).map(([camel, snake]) => [snake, camel]),
    );

    for (const [key, value] of Object.entries(dbFeatures)) {
        const camelKey = reverseMap[key] || key;
        if (typeof value === "boolean") {
            (result as Record<string, boolean>)[camelKey] = value;
        }
    }

    return result;
}

/** Map camelCase feature key to DB snake_case for write */
function camelToDbKey(key: PracticeFeatureKey): string {
    return FEATURE_KEY_TO_DB[key] ?? key;
}

/** Build complete FeatureStatusMap from plan + enabled features */
function buildFeatureStatusMap(
    plan: SubscriptionPlan,
    enabledFeatures: Partial<PracticeFeatures>,
): FeatureStatusMap {
    const planFeatures = PLAN_FEATURES[plan] || [];

    const map = {} as FeatureStatusMap;

    for (const key of PRACTICE_FEATURE_KEYS) {
        const addonActivated = isAddonActivatedPracticeFeature(key);
        const inPlan = addonActivated ? false : planFeatures.includes(key);
        const configurable = CONFIGURABLE_FEATURE_KEYS.includes(key);
        const enabled = addonActivated ? false : inPlan && enabledFeatures[key] !== false;

        map[key] = {
            enabled,
            inPlan,
            configurable,
            lockedReason: addonActivated
                ? "Freischaltung über Tarif & Module erforderlich"
                : inPlan
                  ? undefined
                  : `Benötigt ${planUpgradeLabel(plan)}`,
        };
    }

    return map;
}

function mergeCanonicalEntitlements(
    statusMap: FeatureStatusMap,
    canonicalEnabledFeatures: Partial<PracticeFeatures>,
): FeatureStatusMap {
    const nextMap = { ...statusMap };

    for (const key of PRACTICE_FEATURE_KEYS) {
        if (canonicalEnabledFeatures[key] !== true) continue;

        nextMap[key] = {
            ...nextMap[key],
            enabled: true,
        };
    }

    return nextMap;
}

async function loadCanonicalEntitlements(
    practiceId: string,
): Promise<Partial<PracticeFeatures>> {
    const sb = requireSupabase();

    const results = await Promise.all(
        PRACTICE_FEATURE_KEYS.map(async (featureKey) => {
            const { data, error } = await sb.rpc("check_feature_enabled", {
                p_practice_id: practiceId,
                p_feature_key: camelToDbKey(featureKey),
            });

            return { featureKey, data, error };
        }),
    );

    const failedResult = results.find(
        ({ data, error }) => error || typeof data !== "boolean",
    );

    if (failedResult) {
        logger.warn(
            LOG,
            "Canonical entitlement RPC nicht verfügbar, nutze Plan-/Toggle-Fallback.",
            failedResult.error?.message,
        );
        return {};
    }

    return results.reduce<Partial<PracticeFeatures>>((accumulator, { featureKey, data }) => {
        if (data === true) {
            accumulator[featureKey] = true;
        }
        return accumulator;
    }, {});
}

function planUpgradeLabel(currentPlan: SubscriptionPlan): string {
    switch (currentPlan) {
        case "none":
        case "starter":
            return "Professional-Plan";
        case "professional":
            return "Enterprise-Plan";
        default:
            return "Höheren Plan";
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════════════

export interface FeatureGateData {
    plan: SubscriptionPlan;
    enabledFeatures: Partial<PracticeFeatures>;
    statusMap: FeatureStatusMap;
}

/**
 * Load complete feature gate state for a practice.
 */
export async function getFeatureGate(practiceId: string): Promise<FeatureGateData> {
    const fallback: FeatureGateData = {
        plan: "none",
        enabledFeatures: {},
        statusMap: buildFeatureStatusMap("none", {}),
    };

    if (!practiceId || !isSupabaseConfigured()) return fallback;

    try {
        const sb = requireSupabase();

        const { data, error } = await sb
            .from("practices")
            .select("subscription_plan, enabled_features")
            .eq("id", practiceId)
            .single();

        if (error || !data) {
            logger.warn(LOG, "Konnte Feature-Gate nicht laden:", error?.message);
            return fallback;
        }

        const plan = (data.subscription_plan as SubscriptionPlan) || "none";
        const enabledFeatures = dbFeaturesToCamel(
            data.enabled_features as Record<string, unknown> | null,
        );
        const canonicalEntitlements = await loadCanonicalEntitlements(practiceId);
        const mergedEnabledFeatures = {
            ...enabledFeatures,
            ...canonicalEntitlements,
        };

        return {
            plan,
            enabledFeatures: mergedEnabledFeatures,
            statusMap: mergeCanonicalEntitlements(
                buildFeatureStatusMap(plan, enabledFeatures),
                canonicalEntitlements,
            ),
        };
    } catch (err) {
        logger.error(LOG, "getFeatureGate error:", err);
        return fallback;
    }
}

/**
 * Toggle a single feature on/off.
 * Only allows toggling features that are in the current plan.
 */
export async function toggleFeature(
    practiceId: string,
    feature: PracticeFeatureKey,
    enabled: boolean,
): Promise<void> {
    if (!practiceId || !isSupabaseConfigured()) {
        throw new Error("Praxis-Kontext fehlt.");
    }

    // Read current features
    const { data, error: readError } = await supabase
        .from("practices")
        .select("subscription_plan, enabled_features")
        .eq("id", practiceId)
        .single();

    if (readError || !data) {
        throw new Error("Praxis konnte nicht geladen werden.");
    }

    const plan = (data.subscription_plan as SubscriptionPlan) || "none";
    const planFeatures = PLAN_FEATURES[plan] || [];

    if (isAddonActivatedPracticeFeature(feature)) {
        throw new Error(`Feature "${feature}" wird über Tarif & Module freigeschaltet.`);
    }

    if (!planFeatures.includes(feature)) {
        throw new Error(`Feature "${feature}" ist im aktuellen Plan nicht verfügbar.`);
    }

    // Merge with existing features (snake_case in DB)
    const existing = (data.enabled_features as Record<string, boolean>) || {};
    const dbKey = camelToDbKey(feature);
    const updated = { ...existing, [dbKey]: enabled };

    const { error: writeError } = await supabase
        .from("practices")
        .update({
            enabled_features: updated,
            updated_at: new Date().toISOString(),
        })
        .eq("id", practiceId);

    if (writeError) {
        logger.error(LOG, "toggleFeature write error:", writeError.message);
        throw new Error("Feature konnte nicht aktualisiert werden.");
    }
}

/**
 * Get status for a single feature.
 */
export function getFeatureStatus(
    feature: PracticeFeatureKey,
    plan: SubscriptionPlan,
    enabledFeatures: Partial<PracticeFeatures>,
): FeatureStatus {
    const map = buildFeatureStatusMap(plan, enabledFeatures);
    return map[feature];
}

/**
 * Check if a feature is available and enabled.
 */
export function isFeatureEnabled(
    feature: PracticeFeatureKey,
    plan: SubscriptionPlan,
    enabledFeatures: Partial<PracticeFeatures>,
): boolean {
    const status = getFeatureStatus(feature, plan, enabledFeatures);
    return status.enabled;
}
